/**
 * Assign Guard Form - Microsoft 365 Style
 * Form to assign guards to client sites with shifts
 */

import React, { useEffect, useState } from 'react';
import { makeStyles, Text, Button, Input, Field } from '@fluentui/react-components';
import { SearchRegular, DeleteRegular, ArrowLeftRegular } from '@fluentui/react-icons';
import { useNavigate } from 'react-router-dom';
import { ContentCard } from '../../../components/layout';
import { SearchableDropdown } from '../../../components/common';
import { ApiClient, newToast } from '../../../configurations/index.config';
import { getErrorMessage } from '../../../types';

const useStyles = makeStyles({
  pageHeader: {
    marginBottom: '24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  pageTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#323130',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '16px',
    display: 'block',
  },
  sectionDivider: {
    height: '1px',
    backgroundColor: '#e1dfdd',
    marginTop: '24px',
    marginBottom: '24px',
  },
  formSection: {
    marginBottom: '32px',
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '20px',
    marginBottom: '24px',
  },
  formRow2: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '20px',
    marginBottom: '16px',
  },
  formRow3: {
    display: 'grid',
    gridTemplateColumns: '5fr 5fr 2fr',
    gap: '20px',
    marginBottom: '0',
    alignItems: 'end',
  },
  linkStyle: {
    fontSize: '12px',
    marginTop: '4px',
    display: 'block',
  },
  searchBar: {
    marginBottom: '20px',
    marginTop: '32px',
  },
  tableContainer: {
    marginTop: '24px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  tableHeader: {
    backgroundColor: '#faf9f8',
    borderBottom: '2px solid #e1dfdd',
  },
  th: {
    padding: '14px 16px',
    textAlign: 'left',
    fontSize: '13px',
    fontWeight: '600',
    color: '#323130',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  tr: {
    borderBottom: '1px solid #edebe9',
    ':hover': {
      backgroundColor: '#f9f9f9',
    },
  },
  td: {
    padding: '14px 16px',
    fontSize: '14px',
    color: '#323130',
  },
  emptyState: {
    padding: '80px 20px',
    textAlign: 'center',
    color: '#605e5c',
  },
  emptyStateTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '8px',
  },
  emptyStateText: {
    fontSize: '14px',
    color: '#605e5c',
  },
  submitSection: {
    marginTop: '32px',
    paddingTop: '24px',
    borderTop: '1px solid #e1dfdd',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
  },
  submitButtons: {
    display: 'flex',
    gap: '12px',
  },
  assignmentCount: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#605e5c',
  },
});

interface Client {
  id: number;
  clientid: string;
  client_name: string;
}

interface Site {
  id: number;
  name: string;
  site_code: string;
  gps_code: string;
  client_id: number;
}

interface Shift {
  id: number;
  name: string;
  start_time: string;
  end_time: string;
  clock_count: number;
}

interface Department {
  id: string;
  department_name: string;
  description?: string;
}

interface Employee {
  id: number;
  first_name: string;
  last_name: string;
  department_id?: number;
}

interface AssignmentListItem {
  id: string;
  client_id: number;
  client_name: string;
  site_id: number;
  site_name: string;
  employee_id: number;
  employee_name: string;
  shift_id: number;
  shift_name: string;
  shift_schedule: string;
  start_date: string;
}

export const AssignGuardFormFluentUI: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();

  const [clients, setClients] = useState<Client[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [assignmentList, setAssignmentList] = useState<AssignmentListItem[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState<{
    client_id: number;
    site_id: number;
    shift_id: number;
    employee_id: number;
    start_date: string;
  }>({
    client_id: 0,
    site_id: 0,
    shift_id: 0,
    employee_id: 0,
    start_date: new Date().toISOString().split('T')[0] ?? '',
  });

  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);
  const [selectedShift, setSelectedShift] = useState<Shift | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);

  useEffect(() => {
    fetchDropdownData();
  }, []);

  const fetchDropdownData = async () => {
    try {
      const [clientsRes, sitesRes, shiftsRes, departmentsRes, employeesRes] = await Promise.all([
        ApiClient.get('/get-clients'),
        ApiClient.get('/get-sites'),
        ApiClient.get('/get-shifts'),
        ApiClient.get('/get-departments'),
        ApiClient.get('/get-employee-data'),
      ]);

      if (clientsRes.data.status === 'success') {
        setClients(clientsRes.data.data || []);
      }
      if (sitesRes.data.status === 'success') {
        setSites(sitesRes.data.data || []);
      }
      if (shiftsRes.data.status === 'success') {
        setShifts(shiftsRes.data.data || []);
      }
      if (departmentsRes.data.status === 'success') {
        setDepartments(departmentsRes.data.data || []);
      }
      if (employeesRes.data.status === 'success') {
        // Deduplicate employees by id to handle backend JOIN duplicates
        const employeeData = employeesRes.data.data || [];
        const uniqueEmployees = employeeData.filter(
          (emp: Employee, index: number, self: Employee[]) =>
            index === self.findIndex(e => e.id === emp.id)
        );
        setEmployees(uniqueEmployees);
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
  };

  const handleClientChange = (clientId: number) => {
    const client = clients.find(c => c.id === clientId);
    setSelectedClient(client || null);
    setFormData({ ...formData, client_id: clientId, site_id: 0 });
    setSelectedSite(null);
  };

  const handleSiteChange = (siteId: number) => {
    const site = sites.find(s => s.id === siteId);
    setSelectedSite(site || null);
    setFormData({ ...formData, site_id: siteId });
  };

  const handleShiftChange = (shiftId: number) => {
    const shift = shifts.find(s => s.id === shiftId);
    setSelectedShift(shift || null);
    setFormData({ ...formData, shift_id: shiftId });
  };

  const handleEmployeeChange = (employeeId: number) => {
    const employee = employees.find(e => e.id === employeeId);
    setSelectedEmployee(employee || null);
    setFormData({ ...formData, employee_id: employeeId });
  };

  const getFilteredEmployees = () => {
    if (!selectedDepartmentId) {
      return employees;
    }
    return employees.filter(e => e.department_id === Number(selectedDepartmentId));
  };

  const handleDepartmentChange = (departmentId: string) => {
    setSelectedDepartmentId(departmentId);
    // Clear employee selection when department changes
    setFormData({ ...formData, employee_id: 0 });
    setSelectedEmployee(null);
  };

  const getFilteredSites = () => {
    if (!formData.client_id) {
      return [];
    }
    return sites.filter(s => s.client_id === formData.client_id);
  };

  const calculateHours = () => {
    if (!selectedShift) {
      return '0';
    }
    const start = new Date(`2000-01-01T${selectedShift.start_time}`);
    const end = new Date(`2000-01-01T${selectedShift.end_time}`);
    const diff = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
    return diff.toFixed(1);
  };

  const handleAddToList = () => {
    if (!formData.client_id || !formData.site_id || !formData.shift_id || !formData.employee_id) {
      newToast('error', 'Please fill all required fields');
      return;
    }

    const newAssignment: AssignmentListItem = {
      id: `${Date.now()}-${Math.random()}`,
      client_id: formData.client_id,
      client_name: selectedClient?.client_name || '',
      site_id: formData.site_id,
      site_name: selectedSite?.name || '',
      employee_id: formData.employee_id,
      employee_name: `${selectedEmployee?.first_name} ${selectedEmployee?.last_name}`,
      shift_id: formData.shift_id,
      shift_name: selectedShift?.name || '',
      shift_schedule: `${selectedShift?.start_time} - ${selectedShift?.end_time}`,
      start_date: formData.start_date,
    };

    setAssignmentList([...assignmentList, newAssignment]);
    // Only reset employee selection - keep client, site, and shift for adding multiple employees
    setFormData({ ...formData, employee_id: 0 });
    setSelectedEmployee(null);
    newToast('success', 'Employee added to list');
  };

  const handleRemoveFromList = (id: string) => {
    setAssignmentList(assignmentList.filter(a => a.id !== id));
  };

  const handleSubmitAll = async () => {
    if (assignmentList.length === 0) {
      newToast('error', 'Please add at least one assignment');
      return;
    }

    setSubmitting(true);
    let successCount = 0;
    let failCount = 0;

    try {
      // Submit each assignment individually as per API spec
      for (const assignment of assignmentList) {
        try {
          const payload = {
            client_id: assignment.client_id,
            employee_id: assignment.employee_id,
            site_id: assignment.site_id,
            shift_id: assignment.shift_id,
            start_date: assignment.start_date,
          };

          const response = await ApiClient.post('/add-update-assign-guard', payload);
          if (response.data.status === 'success') {
            successCount++;
          } else {
            failCount++;
            newToast('error', `Failed: ${response.data.message || 'Unknown error'}`);
          }
        } catch (err: unknown) {
          failCount++;
          newToast('error', `Error: ${getErrorMessage(err)}`);
        }
      }

      if (successCount > 0) {
        newToast('success', `${successCount} guard assignment(s) submitted successfully`);
        setAssignmentList([]);
        // Reset form
        setFormData({
          client_id: 0,
          site_id: 0,
          shift_id: 0,
          employee_id: 0,
          start_date: new Date().toISOString().split('T')[0] ?? '',
        });
        setSelectedClient(null);
        setSelectedSite(null);
        setSelectedShift(null);
        setSelectedEmployee(null);
        navigate('/account/guard-operations/assign-guard');
      }

      if (failCount > 0) {
        newToast('error', `${failCount} assignment(s) failed to submit`);
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setSubmitting(false);
  };

  const getFilteredAssignments = () => {
    if (!searchQuery) {
      return assignmentList;
    }
    return assignmentList.filter(
      a =>
        a.client_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        a.site_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        a.employee_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        a.shift_name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  const filteredList = getFilteredAssignments();

  return (
    <div>
      {/* Header with Back Button */}
      <div className={styles.pageHeader}>
        <Text className={styles.pageTitle}>Assign Guard Form</Text>
        <Button
          appearance="subtle"
          icon={<ArrowLeftRegular />}
          onClick={() => navigate('/account/guard-operations/assign-guard')}
        >
          Go Back
        </Button>
      </div>

      {/* Form Section */}
      <ContentCard>
        <Text className={styles.sectionTitle}>Assignment Details</Text>

        {/* Section 1: Site Information */}
        <div className={styles.formSection}>
          <div className={styles.formRow}>
            <Field label="Client" required>
              <SearchableDropdown
                placeholder="Search clients..."
                options={clients.map(c => ({ id: c.id, label: c.client_name }))}
                selectedValue={formData.client_id ? String(formData.client_id) : ''}
                onSelect={value => handleClientChange(Number(value))}
                required
              />
            </Field>

            <Field label="Client Site" required>
              <SearchableDropdown
                key={formData.client_id}
                placeholder="Search sites..."
                options={getFilteredSites().map(s => ({ id: s.id, label: s.name }))}
                selectedValue={formData.site_id ? String(formData.site_id) : ''}
                onSelect={value => handleSiteChange(Number(value))}
                disabled={!formData.client_id}
                required
              />
            </Field>

            <Field label="Site Code">
              <Input
                value={selectedSite?.site_code || ''}
                readOnly
                disabled
                size="medium"
                style={{ backgroundColor: '#faf9f8' }}
              />
            </Field>

            <Field label="Site Address">
              <Input
                value={selectedSite?.gps_code || 'Site address'}
                readOnly
                disabled
                size="medium"
                style={{ backgroundColor: '#faf9f8' }}
              />
            </Field>
          </div>
        </div>

        <div className={styles.sectionDivider} />

        {/* Section 2: Shift Information */}
        <div className={styles.formSection}>
          {/* Row 1: Shift, Start Time, End Time */}
          <div className={styles.formRow2}>
            <Field label="Shift" required>
              <SearchableDropdown
                placeholder="Search shifts..."
                options={shifts.map(s => ({ id: s.id, label: s.name }))}
                selectedValue={formData.shift_id ? String(formData.shift_id) : ''}
                onSelect={value => handleShiftChange(Number(value))}
                required
              />
            </Field>

            <Field label="Start Time">
              <Input
                value={selectedShift?.start_time || ''}
                readOnly
                disabled
                size="medium"
                style={{ backgroundColor: '#faf9f8' }}
              />
            </Field>

            <Field label="End Time">
              <Input
                value={selectedShift?.end_time || ''}
                readOnly
                disabled
                size="medium"
                style={{ backgroundColor: '#faf9f8' }}
              />
            </Field>
          </div>

          {/* Row 2: Hours, Required Clock Count, Start Date */}
          <div className={styles.formRow2}>
            <Field label="Hours">
              <Input
                value={calculateHours()}
                readOnly
                disabled
                size="medium"
                style={{ backgroundColor: '#faf9f8' }}
              />
            </Field>

            <Field label="Required Clock Count">
              <Input
                value={String(selectedShift?.clock_count || 0)}
                readOnly
                disabled
                size="medium"
                style={{ backgroundColor: '#faf9f8' }}
              />
            </Field>

            <Field label="Start Date" required>
              <Input
                type="date"
                value={formData.start_date}
                onChange={e => setFormData({ ...formData, start_date: e.target.value })}
                size="medium"
              />
            </Field>
          </div>
        </div>

        <div className={styles.sectionDivider} />

        {/* Section 3: Employee Assignment */}
        <div className={styles.formSection}>
          <div className={styles.formRow3}>
            <Field label="Department (Filter)">
              <SearchableDropdown
                placeholder="Filter by department..."
                options={[
                  { id: '', label: 'All Departments' },
                  ...departments.map(d => ({
                    id: d.id,
                    label: d.department_name,
                  })),
                ]}
                selectedValue={selectedDepartmentId}
                onSelect={value => handleDepartmentChange(value)}
              />
            </Field>

            <Field label="Employee" required>
              <SearchableDropdown
                placeholder="Search employees..."
                options={getFilteredEmployees().map(e => ({
                  id: e.id,
                  label: `${e.first_name} ${e.last_name}`,
                }))}
                selectedValue={formData.employee_id ? String(formData.employee_id) : ''}
                onSelect={value => handleEmployeeChange(Number(value))}
                required
              />
            </Field>

            <Button
              appearance="primary"
              onClick={handleAddToList}
              size="large"
              style={{ height: '40px' }}
            >
              Add To List
            </Button>
          </div>
        </div>
      </ContentCard>

      {/* Search Bar */}
      <div className={styles.searchBar}>
        <Input
          placeholder="Search table..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          contentBefore={<SearchRegular />}
        />
      </div>

      {/* Assignments Table */}
      <div className={styles.tableContainer}>
        <ContentCard noPadding>
          {filteredList.length === 0 ? (
            <div className={styles.emptyState}>
              <Text className={styles.emptyStateTitle}>No assignments added yet</Text>
              <Text className={styles.emptyStateText}>
                Fill the form above and click "Add To List" to begin adding guard assignments
              </Text>
            </div>
          ) : (
            <>
              <table className={styles.table}>
                <thead className={styles.tableHeader}>
                  <tr>
                    <th className={styles.th}>Client</th>
                    <th className={styles.th}>Client Site</th>
                    <th className={styles.th}>Employee</th>
                    <th className={styles.th}>Shift Schedule</th>
                    <th className={styles.th}>Start Date</th>
                    <th className={styles.th}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredList.map(item => (
                    <tr key={item.id} className={styles.tr}>
                      <td className={styles.td}>{item.client_name}</td>
                      <td className={styles.td}>{item.site_name}</td>
                      <td className={styles.td}>{item.employee_name}</td>
                      <td className={styles.td}>
                        <Text weight="semibold">{item.shift_name}</Text>
                        <Text
                          size={200}
                          style={{ display: 'block', color: '#605e5c', marginTop: '2px' }}
                        >
                          {item.shift_schedule}
                        </Text>
                      </td>
                      <td className={styles.td}>{item.start_date}</td>
                      <td className={styles.td}>
                        <Button
                          size="small"
                          appearance="subtle"
                          icon={<DeleteRegular />}
                          onClick={() => handleRemoveFromList(item.id)}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Submit Section */}
              <div className={styles.submitSection}>
                <Text className={styles.assignmentCount}>
                  {assignmentList.length} assignment{assignmentList.length !== 1 ? 's' : ''} ready
                  to submit
                </Text>
                <div className={styles.submitButtons}>
                  <Button
                    appearance="secondary"
                    size="large"
                    onClick={() => navigate('/account/guard-operations/assign-guard')}
                  >
                    Cancel
                  </Button>
                  <Button
                    appearance="primary"
                    size="large"
                    onClick={handleSubmitAll}
                    disabled={submitting}
                  >
                    {submitting ? 'Submitting...' : 'Submit All Assignments'}
                  </Button>
                </div>
              </div>
            </>
          )}
        </ContentCard>
      </div>
    </div>
  );
};

export default AssignGuardFormFluentUI;
